/**
 * Client-side pre-validation for the 素材 / 说话人 import sheets (docs/UX.md 12/13).
 *
 * The server re-checks everything; this exists so a course-team member sees
 * *which row, which char and why* before anything is sent — 红线 14.5 in
 * particular (a char without a parseable locked pinyin never enters the corpus).
 */
import {
  PinyinError,
  materialImportSchema,
  parsePinyin,
  speakerImportSchema,
} from "@xinghai/shared";
import type { MaterialImportPayload, SpeakerImportPayload } from "@xinghai/shared";

export interface ImportIssue {
  /** 1-based row number in the pasted array. `0` = the document itself. */
  row: number;
  /** Material / speaker id when it could be read. */
  id?: string;
  /** Offending character (素材 import only). */
  char?: string;
  /** 0-based char index inside the material. */
  charIndex?: number;
  message: string;
}

export interface ImportValidation<T> {
  ok: boolean;
  issues: ImportIssue[];
  /** Only set when `ok` — this is exactly what gets POSTed. */
  payload: T | null;
  /** Rows that would be imported. */
  count: number;
}

function failure<T>(issues: ImportIssue[]): ImportValidation<T> {
  return { ok: false, issues, payload: null, count: 0 };
}

/** Accepts a bare JSON array or the wrapped `{ materials: [...] }` envelope. */
function envelope(raw: unknown, key: "materials" | "speakers"): unknown {
  if (Array.isArray(raw)) return { [key]: raw };
  if (raw && typeof raw === "object" && key in (raw as Record<string, unknown>)) return raw;
  return raw;
}

/** `[ "materials", 3, "chars", 1, "tone" ]` → row 4. */
function rowOf(path: ReadonlyArray<PropertyKey>): number {
  for (const seg of path) if (typeof seg === "number") return seg + 1;
  return 0;
}

function pathTail(path: ReadonlyArray<PropertyKey>): string {
  return path.map((p) => String(p)).join(".");
}

/** `Blob.text()` is missing in some jsdom builds, so fall back to `FileReader`. */
export function readTextFile(file: File): Promise<string> {
  if (typeof (file as Blob).text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("读取文件失败"));
    reader.readAsText(file);
  });
}

export function parseJsonInput(text: string): { value: unknown; error: string | null } {
  if (!text.trim()) return { value: null, error: "文件是空的，请选择包含 JSON 数组的文件。" };
  try {
    return { value: JSON.parse(text) as unknown, error: null };
  } catch (err) {
    return { value: null, error: `不是合法的 JSON：${(err as Error).message}` };
  }
}

/**
 * 素材导入校验：`materialImportSchema` + `parsePinyin()` on every
 * `chars[].pinyin_locked`.
 */
export function validateMaterialImport(raw: unknown): ImportValidation<MaterialImportPayload> {
  const parsed = materialImportSchema.safeParse(envelope(raw, "materials"));
  if (!parsed.success) {
    return failure(
      parsed.error.issues.map((i) => ({
        row: rowOf(i.path),
        message: `${pathTail(i.path) || "整体结构"}：${i.message}`,
      })),
    );
  }

  const issues: ImportIssue[] = [];
  const seen = new Set<string>();
  parsed.data.materials.forEach((m, mi) => {
    const row = mi + 1;
    if (seen.has(m.id)) {
      issues.push({ row, id: m.id, message: `素材编号 ${m.id} 在本次导入里重复出现。` });
    }
    seen.add(m.id);

    m.chars.forEach((ch, ci) => {
      const py = (ch.pinyin_locked ?? "").trim();
      if (!py) {
        issues.push({
          row,
          id: m.id,
          char: ch.char,
          charIndex: ci,
          message: "缺少锁定拼音（红线 14.5：禁止由汉字回退推拼音）。",
        });
        return;
      }
      try {
        parsePinyin(py);
      } catch (err) {
        issues.push({
          row,
          id: m.id,
          char: ch.char,
          charIndex: ci,
          message:
            err instanceof PinyinError
              ? `锁定拼音「${py}」无法解析：${err.message}`
              : `锁定拼音「${py}」无法解析。`,
        });
      }
    });
  });

  if (issues.length > 0) return failure(issues);
  return { ok: true, issues: [], payload: parsed.data, count: parsed.data.materials.length };
}

/** 说话人名单导入校验：`speakerImportSchema` + duplicate detection. */
export function validateSpeakerImport(raw: unknown): ImportValidation<SpeakerImportPayload> {
  const parsed = speakerImportSchema.safeParse(envelope(raw, "speakers"));
  if (!parsed.success) {
    return failure(
      parsed.error.issues.map((i) => ({
        row: rowOf(i.path),
        message: `${pathTail(i.path) || "整体结构"}：${i.message}`,
      })),
    );
  }

  const issues: ImportIssue[] = [];
  const codes = new Set<string>();
  const usernames = new Set<string>();
  parsed.data.speakers.forEach((s, i) => {
    const row = i + 1;
    if (codes.has(s.speaker_code)) {
      issues.push({ row, id: s.speaker_code, message: `说话人编号 ${s.speaker_code} 重复。` });
    }
    codes.add(s.speaker_code);
    if (usernames.has(s.username)) {
      issues.push({ row, id: s.speaker_code, message: `用户名 ${s.username} 重复。` });
    }
    usernames.add(s.username);
  });

  if (issues.length > 0) return failure(issues);
  return { ok: true, issues: [], payload: parsed.data, count: parsed.data.speakers.length };
}
